'use client'

import {
  type ItemAddedToCart,
  type ItemInViewCart,
  type ItemRemovedFromCart,
  type TrackingAddToCart,
  type TrackingRemoveFromCart,
  type TrackingViewCart,
} from '@/lib/tracking/services/tracking.eventTypes'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { lastPageTrackedAtom, trackingConsentAtom } from '@/lib/tracking/services/tracking.store'
import { assertNever } from '@/lib/typescript/assertNever'
import { isItemDefined } from '@/lib/utils/array'
import { useLazyQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import type { OrderItemsSectionFragment } from '@gql-generated/graphql'
import {
  Availability,
  type CartItemDeliveryStockDetails,
  CartItemTrackingMultipleDocument,
  type CartTracking_CartFragment,
  CartUserTrackingDocument,
  OrderCurrency,
  ShippingDelay,
} from '@gql-generated/graphql'
import type {
  useCartItemTrackingMultipleLazyQuery,
  useCartUserTrackingLazyQuery,
} from '@gql-generated/types'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import { getTrackingItemSource } from './cart.tracking.addToCart'

type ProductData = {
  reference?: string | null
  name?: string | null
  type?: string | null
  parentReference?: string | null
  price?: {
    exclTax?: number | null
    tag?: string | null
  } | null
  brand?: {
    name?: string | null
  } | null
  analytics?: {
    promotionStatus?: string | null
  } | null
  canAddToCart?: boolean | null
  stock?: { availability?: (Availability | null)[] | null; isSellable?: boolean | null } | null
  nomenclatureFamily?: string | null
  nomenclatureGroup?: string | null
  promoPriceCode?: string | null
}

type CartTrackingItem = {
  reference: string
  quantity: number
  source: string
  price: number
  analyticsPromotionStatus?: string | null
}

type CartViewTrackingItem = CartTrackingItem & {
  deliveryDetail: CartItemDeliveryStockDetails | null
  availableClickAndCollect: boolean
  analyticsPromotionStatus?: string | null
}

/**
 * Build analytics promotion label for tracking from backend-provided fields.
 *
 * The backend exposes:
 * - Product.analytics.promotionStatus for product-level promos
 * - CartItem.analyticsPromotionStatus for cart-level/web promos
 * - OrderItem.analyticsPromotionStatus for order-level/web promos
 *
 * We concatenate the non-empty values with " | ".
 * If everything is empty, we return "no promo".
 */
export const getTrackingPromotionLabel = (
  productPromotionStatus?: string | null,
  cartPromotionStatus?: string | null,
  orderPromotionStatus?: string | null,
): string => {
  const promotions = [productPromotionStatus, cartPromotionStatus, orderPromotionStatus].filter(
    (value): value is string => !!value && value.trim().length > 0,
  )

  return promotions.join(' | ') || 'no promo'
}

/**
 * Get purchase mode string for tracking from product availability and sellable status
 */
export const getTrackingCartItemPurchaseMode = (
  availability: (Availability | null)[],
  isSellable: boolean,
  isAuthenticated: boolean,
): string => {
  // temporarily placing it here before sync with catalogue squad
  const hasOnline = availability?.some((a) => a === Availability.Online)
  const hasInStore = availability?.some(
    (a) => a === Availability.Store || a === Availability.StoreOnly,
  )
  const isStoreOnly = availability?.some((a) => a === Availability.StoreOnly)

  if (!isAuthenticated) return 'non-achetable - user déconnecté'
  if (!isSellable && isAuthenticated) return 'non-achetable'

  if (hasOnline && hasInStore) {
    return 'achat en ligne OU retrait express'
  }

  if (isStoreOnly) {
    return 'express uniquement'
  }

  if (hasOnline) {
    return 'achat en ligne'
  }

  return 'retrait express'
}

/**
 * Get delivery delay for tracking from cart item details
 */
export const getTrackingCartItemDeliveryDelay = (
  deliveryDetail: CartItemDeliveryStockDetails | null,
  isCartClickAndCollect: boolean,
  isAvailableClickAndCollect: boolean,
): string => {
  if (isCartClickAndCollect && isAvailableClickAndCollect) {
    return 'c&c'
  }

  if (!deliveryDetail) return ''

  if (deliveryDetail.realDeliveryDate && deliveryDetail.date) {
    return 'délai réel'
  }

  if (deliveryDetail.availability === ShippingDelay.Hours_24To_48) {
    return 'sous 24/48h'
  }

  if (deliveryDetail.availability === ShippingDelay.Days_3To_5) {
    return 'sous 3/5 jours'
  }

  if (deliveryDetail.availability === ShippingDelay.Weeks_4) {
    return 'sous 4 semaines'
  }

  if (deliveryDetail.availability === ShippingDelay.Unknown) {
    return ''
  }

  assertNever(deliveryDetail.availability)
}

/**
 * Get delivery delay for tracking from cart item section
 */
export const getTrackingCartItemDeliveryDelayViaSectionInfo = (
  sectionInfo: OrderItemsSectionFragment['info'],
) => {
  const isReal = sectionInfo.__typename === 'OrderItemsSectionInfoReal'
  const isEstimated = sectionInfo.__typename === 'OrderItemsSectionInfoEstimated'
  const isClickAndCollect = sectionInfo.__typename === 'OrderItemsSectionInfoClickAndCollect'
  const isNoDeliveryTime = sectionInfo.__typename === 'OrderItemsSectionInfoNoDeliveryTime'

  if (isReal) {
    return 'délai réel'
  }

  if (isEstimated) {
    switch (sectionInfo.delay) {
      case ShippingDelay.Hours_24To_48:
        return 'sous 24/48h'
      case ShippingDelay.Days_3To_5:
        return 'sous 3/5 jours'
      case ShippingDelay.Weeks_4:
        return 'sous 4 semaines'
      case ShippingDelay.Unknown:
        return ''
      default:
        assertNever(sectionInfo.delay)
    }
  }

  if (isClickAndCollect) {
    return 'c&c'
  }

  if (isNoDeliveryTime) {
    return 'no delivery time'
  }

  assertNever(sectionInfo)
}

const TRACKING_ITEM_DEFAULTS = {
  item_variants_amount: '',
  item_discount: '',
  item_category3: '',
  item_market_price: '',
  item_acgc_price: '',
  item_public_price: 'false',
  item_stock_info: '',
  item_reconditionne: '',
} as const

function createBaseTrackingItem(
  item: CartTrackingItem,
  productData: ProductData | undefined,
  analyticsPromotionStatus?: string | null,
  orderPromotionStatus?: string | null,
) {
  return {
    item_id: item.reference,
    item_nature: productData?.type?.toLowerCase() || 'article',
    item_name: productData?.name || '',
    item_price: item?.price || productData?.price?.exclTax || 0,
    item_brand: productData?.brand?.name || '',
    item_category: productData?.nomenclatureFamily || '',
    item_category2: productData?.nomenclatureGroup || '',
    item_code_p: productData?.parentReference || '',
    item_list_name: item.source,
    item_purchase_mode: getTrackingCartItemPurchaseMode(
      productData?.stock?.availability || [],
      productData?.stock?.isSellable ?? productData?.canAddToCart ?? false,
      true,
    ),
    item_tag: productData?.price?.tag || '',
    item_quantity: item.quantity,
    item_promotion: getTrackingPromotionLabel(
      productData?.analytics?.promotionStatus,
      analyticsPromotionStatus || item.analyticsPromotionStatus,
      orderPromotionStatus,
    ),
    ...TRACKING_ITEM_DEFAULTS,
  }
}

async function fetchProductsData(
  items: CartTrackingItem[],
  cartQuery: ReturnType<typeof useCartItemTrackingMultipleLazyQuery>[0],
  userQuery: ReturnType<typeof useCartUserTrackingLazyQuery>[0],
): Promise<{
  products: ProductData[]
  currency: string
  totalExclTax: number
  hasCoupon: boolean
}> {
  if (items.length === 0) {
    return { products: [], currency: OrderCurrency.Eur, totalExclTax: 0, hasCoupon: false }
  }

  const references = items.map((item) => item.reference)
  const [{ data: cartData }, { data: userData }] = await Promise.all([
    cartQuery({ variables: { references } }),
    userQuery(),
  ])

  return {
    products: cartData?.productsByReferences || [],
    currency: userData?.currentUser?.checkout?.cart?.currency || 'EUR',
    totalExclTax: userData?.currentUser?.checkout?.cart?.totalExclTax || 0,
    hasCoupon: !!userData?.currentUser?.checkout?.cart?.couponCode,
  }
}

function handleTrackingError(error: unknown, context: string): void {
  console.error(`Error in ${context}:`, error)
}

gql(/* GraphQL */ `
  query AddToCartTracking($reference: String!) {
    productByReference(reference: $reference) {
      name
      reference
      type
      parentReference
      price {
        exclTax
        tag
      }
      brand {
        name
        urlKey
      }
      nomenclatureFamily
      nomenclatureGroup
      promoPriceCode
      canAddToCart
      stock {
        availability
        isSellable
      }
      analytics {
        promotionStatus
      }
    }
    currentUser {
      id
      checkout {
        cart {
          id
          currency
          couponCode
        }
      }
    }
  }
`)

gql(/* GraphQL */ `
  query CartItemTrackingMultiple($references: [String!]!) {
    productsByReferences(references: $references) {
      name
      reference
      parentReference
      type
      price {
        exclTax
        tag
      }
      brand {
        name
        urlKey
      }
      nomenclatureFamily
      nomenclatureGroup
      promoPriceCode
      canAddToCart
      stock {
        availability
        isSellable
      }
      analytics {
        promotionStatus
      }
    }
  }

  query CartUserTracking {
    currentUser {
      id
      checkout {
        cart {
          id
          currency
          totalExclTax
          couponCode
        }
      }
    }
  }
`)

gql(/* GraphQL */ `
  fragment CartTracking_Cart on Cart {
    itemsGroupedByEarliestDeliveryDate {
      items {
        ...CartTracking_Item
      }
    }
    isClickAndCollectActivated
    totalExclTax
  }

  fragment CartTracking_Item on CartItem {
    id
    analyticsPromotionStatus
    product {
      reference
    }
    quantity
    unitPrice {
      exclTax
    }
    deliveryDetails {
      date
      quantity
      availability
      realDeliveryDate
    }
    availableClickAndCollect
    totalExclTax
  }
`)

/**
 * Add to cart tracking hook
 */
export const useAddToCartTracking = () => {
  const [addToCartTrackingQuery] = useLazyQuery(CartItemTrackingMultipleDocument)
  const [userTrackingQuery] = useLazyQuery(CartUserTrackingDocument)

  const isTrackingEnabled = useAtomValue(trackingConsentAtom)

  const pushAddToCartEvent = useCallback(
    async (items: CartTrackingItem[]) => {
      if (!isTrackingEnabled) return

      if (items.length === 0) return

      try {
        const { products: productsData, currency } = await fetchProductsData(
          items,
          addToCartTrackingQuery,
          userTrackingQuery,
        )

        const trackingItems = items.map((item): ItemAddedToCart => {
          const productData = productsData.find((p) => p?.reference === item.reference)

          return createBaseTrackingItem(item, productData)
        })

        const trackingData: TrackingAddToCart = {
          event: 'add_to_cart',
          dev_timestamp: Date.now().toString(),
          ecommerce: {
            currency,
            items: trackingItems,
          },
        }

        pushToGtmDataLayer(trackingData)
      } catch (error) {
        handleTrackingError(error, 'addToCart tracking')
      }
    },
    [addToCartTrackingQuery, userTrackingQuery, isTrackingEnabled],
  )

  return pushAddToCartEvent
}

/**
 * Remove from cart tracking hook
 */
export const useRemoveFromCartTracking = () => {
  const isTrackingEnabled = useAtomValue(trackingConsentAtom)

  const [removeFromCartTrackingQuery] = useLazyQuery(CartItemTrackingMultipleDocument)
  const [userTrackingQuery] = useLazyQuery(CartUserTrackingDocument)

  const pushRemoveFromCartEvent = useCallback(
    async (items: CartTrackingItem[]) => {
      if (!isTrackingEnabled) return

      if (items.length === 0) return

      try {
        const { products: productsData, currency } = await fetchProductsData(
          items,
          removeFromCartTrackingQuery,
          userTrackingQuery,
        )

        const trackingItems = items.map((item): ItemRemovedFromCart => {
          const productData = productsData.find((p) => p?.reference === item.reference)

          return createBaseTrackingItem(item, productData)
        })

        const trackingData: TrackingRemoveFromCart = {
          event: 'remove_from_cart',
          dev_timestamp: Date.now().toString(),
          ecommerce: {
            currency,
            items: trackingItems,
          },
        }

        pushToGtmDataLayer(trackingData)
      } catch (error) {
        handleTrackingError(error, 'removeFromCart tracking')
      }
    },
    [removeFromCartTrackingQuery, userTrackingQuery, isTrackingEnabled],
  )

  return pushRemoveFromCartEvent
}

/**
 * Cart view confirmation hook
 */
const useViewCartEventPusher = () => {
  const isTrackingEnabled = useAtomValue(trackingConsentAtom)
  const [viewCartTrackingQuery] = useLazyQuery(CartItemTrackingMultipleDocument)
  const [userTrackingQuery] = useLazyQuery(CartUserTrackingDocument)

  return useCallback(
    async (items: CartViewTrackingItem[], cartCc: boolean = false) => {
      if (!isTrackingEnabled) return

      if (items.length === 0) return

      const {
        products: productsData,
        currency,
        totalExclTax,
      } = await fetchProductsData(items, viewCartTrackingQuery, userTrackingQuery)

      const trackingItems = items.map((item, index) => {
        const productData = productsData.find((p) => p?.reference === item.reference)

        return {
          ...createBaseTrackingItem(item, productData, item.analyticsPromotionStatus),
          item_index: index.toString(),
          item_delivery_delay: getTrackingCartItemDeliveryDelay(
            item.deliveryDetail,
            cartCc,
            item.availableClickAndCollect,
          ),
        } satisfies ItemInViewCart
      })

      const trackingData: TrackingViewCart = {
        event: 'view_cart',
        'cart_c&c': cartCc ? 'true' : 'false',
        is_da: '',
        dev_timestamp: Date.now().toString(),
        ecommerce: {
          currency,
          value: totalExclTax,
          items: trackingItems,
        },
      }

      pushToGtmDataLayer(trackingData)
    },
    [viewCartTrackingQuery, userTrackingQuery, isTrackingEnabled],
  )
}

/**
 * Cart view tracking hook
 * @param options.skipPageCheck - Skip the lastPageTrackedAtom check
 */
export const useCartViewTracking = (
  cart?: CartTracking_CartFragment,
  options?: { skipPageCheck?: boolean },
) => {
  const lastPageTracked = useAtomValue(lastPageTrackedAtom)
  const pushViewCartEvent = useViewCartEventPusher()
  const lastTrackedPayloadRef = useRef<string | null>(null)
  const eventPushed = useRef(false)

  useEffect(() => {
    if (!options?.skipPageCheck && lastPageTracked.pageCategory !== 'panier') {
      lastTrackedPayloadRef.current = null

      return
    }

    if (!cart || !cart.itemsGroupedByEarliestDeliveryDate?.length) return

    const {
      itemsGroupedByEarliestDeliveryDate: groupedItems,
      isClickAndCollectActivated,
      totalExclTax,
    } = cart

    const trackingItems = groupedItems
      .flatMap((section) => section.items)
      .filter(isItemDefined)
      .map((item) => ({
        ...item,
        reference: item.product?.reference ?? '',
        quantity: item.quantity,
        source:
          (item.product?.reference && getTrackingItemSource(item.product?.reference)) ||
          'page panier',
        deliveryDetail: item.deliveryDetails?.[0] ?? null,
        availableClickAndCollect: item.availableClickAndCollect,
        price: item.unitPrice.exclTax ?? 0,
      }))

    const trackingPayloadKey = JSON.stringify({
      totalExclTax,
      cartCc: isClickAndCollectActivated,
    })

    if (lastTrackedPayloadRef.current === trackingPayloadKey) {
      return
    }

    lastTrackedPayloadRef.current = trackingPayloadKey

    const trackCartView = async () => {
      try {
        await pushViewCartEvent(trackingItems, isClickAndCollectActivated)
      } catch (error) {
        lastTrackedPayloadRef.current = null
        handleTrackingError(error, 'cart view tracking effect')
      }
    }

    if (!eventPushed.current) trackCartView()
    eventPushed.current = true
  }, [cart, lastPageTracked, pushViewCartEvent, options?.skipPageCheck])
}
